import { SETUP_STEPS, SIGNAL_KEY_BY_STEP } from '@/constants/setupSteps';
import { isFeatureEnabled } from '@/lib/feature-flag';
import type { SetupProgress, SetupSignals, SetupStep, SetupStepId } from '@/types';

/**
 * Steps whose feature flag is on, plus every unflagged step. The one place the
 * gate is applied, so the checklist, the revalidation map and the store's
 * fetchers cannot disagree about which steps exist.
 */
function enabledSteps() {
  return SETUP_STEPS.filter(step => !step.flag || isFeatureEnabled(step.flag));
}

/** False while a step's feature flag is off — nothing should render or fetch it. */
export function isStepEnabled(stepId: SetupStepId): boolean {
  return enabledSteps().some(step => step.id === stepId);
}

/**
 * Completion predicate per step. Kept pure and in one place so the top bar, the
 * dashboard widget and the tests can never disagree about what "done" means.
 */
export function isStepComplete(stepId: SetupStepId, signals: SetupSignals): boolean {
  switch (stepId) {
    case 'brand-dna':
      // The BE only reports 100 once identity, goals, content and voice are all
      // filled, so this is the whole definition of a complete Brand DNA.
      return signals.brandDnaPercent === 100;
    case 'knowledge-base':
      // `indexed` (not merely uploaded) — an unprocessed document contributes
      // nothing to generation, so counting it would overstate readiness.
      return (signals.knowledgeBaseIndexedCount ?? 0) > 0;
    case 'competitors':
      return (signals.competitorCount ?? 0) > 0;
    case 'review-queue':
      return (signals.reviewedPostCount ?? 0) > 0;
    case 'chat':
      return (signals.chatConversationCount ?? 0) > 0;
    case 'telegram':
      // Strict `=== true`: the signal is a tri-state, and both `null` (not read
      // yet) and a failed read's fallback must read as unlinked.
      return signals.telegramLinked === true;
  }
}

/** True once the signal a step depends on has been fetched at least once. */
export function isStepKnown(stepId: SetupStepId, signals: SetupSignals): boolean {
  return signals[SIGNAL_KEY_BY_STEP[stepId]] !== null;
}

/** Route prefix → the step it can change, for route-based revalidation. */
export function stepForPath(pathname: string): SetupStepId | undefined {
  for (const step of enabledSteps()) {
    const routes = [step.route, ...(step.alsoAffectedBy ?? [])];
    if (routes.some(route => pathname.startsWith(route))) return step.id;
  }
  return undefined;
}

/** Build the full view model the setup surfaces render from. */
export function deriveSetupProgress(signals: SetupSignals): SetupProgress {
  const flags = enabledSteps().map(def => ({
    def,
    completed: isStepComplete(def.id, signals),
    known: isStepKnown(def.id, signals),
  }));

  // Optional steps render as rows but never count: they are offered extras, and
  // one joining the denominator would un-complete every finished account.
  const nextIndex = flags.findIndex(f => !f.completed && !f.def.optional);
  const steps: SetupStep[] = flags.map((f, i) => ({
    ...f.def,
    completed: f.completed,
    known: f.known,
    isNext: i === nextIndex,
  }));

  const counted = steps.filter(s => !s.optional);
  const completedCount = counted.filter(s => s.completed).length;
  const totalCount = counted.length;

  return {
    steps,
    completedCount,
    totalCount,
    percent: Math.round((completedCount / totalCount) * 100),
    nextStep: nextIndex === -1 ? null : steps[nextIndex],
    // Counted steps only: an optional step whose signal never resolves must not
    // hold the whole checklist off the screen.
    hydrated: counted.every(s => s.known),
    isComplete: completedCount === totalCount,
  };
}
